import { Flex, Text } from '@atlaskit/primitives';
import { type FallbackRender, ErrorBoundary } from '@sentry/react';
import React, { Suspense, lazy, type ReactNode, useCallback } from 'react';
import { useIntl } from 'react-intl-next';

import { DateRange } from './date/DateRange';
import { IconWithText } from './iconWithText/IconWithText';
import { messages } from './messages';
import PageStatus from './pageStatus/PageStatus';
import Tag from './tag/Tag';
import { type FieldTypeWithOnlyValues } from './types';

const RenderDatasourceComponent = lazy(() => import('./RenderDatasourceComponent'));

export * from './types';

export type PropsRenderComponentType = (item: FieldTypeWithOnlyValues) => React.ReactNode;

const unreachable = (value: never) => {
  throw new Error(`Not implemented: ${value}`);
};

const FieldValueErrorBoundary: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { formatMessage } = useIntl();

  const fallback: FallbackRender = useCallback(
    () => (
      <Text color={'color.text.danger'}>
        {formatMessage(messages['datasources-render-component.cannot-render-text'])}
      </Text>
    ),
    [formatMessage]
  );

  return (
    <ErrorBoundary
      beforeCapture={(scope) => {
        scope.setLevel('error');
        scope.setTag('component', 'FieldValue');
        scope.setContext('fieldValue', {
          component: 'DatasourcesRenderComponent',
        });
      }}
      fallback={fallback}
    >
      {children}
    </ErrorBoundary>
  );
};

const RenderComponent: React.FC<{ item: FieldTypeWithOnlyValues }> = ({ item }) => {
  switch (item.type) {
    case 'boolean':
    case 'date':
    case 'datetime':
    case 'link':
    case 'number':
    case 'status':
    case 'string':
    case 'time':
    case 'richtext':
    case 'user':
      return (
        <Suspense>
          <RenderDatasourceComponent item={item} />
        </Suspense>
      );
    case 'daterange':
      if ('start' in item.values[0] && 'end' in item.values[0]) {
        return (
          <DateRange startDate={item.values[0].start} endDate={item.values[0].end} />
        );
      }
      return (
        <Suspense>
          <RenderDatasourceComponent item={item} />
        </Suspense>
      );
    case 'icon':
      if ('text' in item.values[0]) {
        return (
          <Suspense>
            <RenderDatasourceComponent item={item} />
          </Suspense>
        );
      }

      return (
        <Suspense>
          <IconWithText itemText={item.text}>
            <RenderDatasourceComponent item={item} />
          </IconWithText>
        </Suspense>
      );
    case 'tag':
      return (
        <Flex gap="space.050" wrap="wrap">
          {item.values.map((tag, key) => (
            <Tag key={key} text={tag.text} />
          ))}
        </Flex>
      );
    case 'html':
      return (
        <span dangerouslySetInnerHTML={{ __html: item.values.join('\n') || '' }}></span>
      );
    case 'pageStatus':
      return item.values.map((status) => (
        <PageStatus key={status.text} status={status} />
      ));
    default:
      return unreachable(item);
  }
};

const DatasourcesRenderComponent: React.FC<{ item: FieldTypeWithOnlyValues }> = ({
  item,
}) => {
  return (
    <FieldValueErrorBoundary>
      <RenderComponent item={item} />
    </FieldValueErrorBoundary>
  );
};

export default DatasourcesRenderComponent;
